import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Sparkles, ChefHat, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, EmptyState } from "@/components/Logo";
import { ChangeBadge } from "@/components/invoices/InvoiceDetailDialog";
import AiFillDialog from "@/components/recipes/AiFillDialog";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { eur, pct, foodCostTone, fold, catLabel } from "@/lib/format";
import { t } from "@/i18n";

const SORTS = {
  margin: [() => t("recipes.marge"), (a, b) => b.gross_margin - a.gross_margin],
  fc_desc: [t("recipes.cout_matiere"), (a, b) => b.food_cost_pct - a.food_cost_pct],
  fc_asc: [t("recipes.cout_matiere_2"), (a, b) => a.food_cost_pct - b.food_cost_pct],
  name: [t("common.nom"), (a, b) => a.name.localeCompare(b.name)],
};

function FoodCostBar({ value, target }) {
  const color = value <= target ? "bg-emerald-500" : value <= target + 5 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="flex items-center justify-end gap-2">
      <div className="relative hidden h-1.5 w-20 overflow-hidden rounded-full bg-muted sm:block">
        <div className={`h-full ${color}`} style={{ width: `${Math.min(100, value * 2)}%` }} />
        <div className="absolute top-0 h-full w-px bg-foreground/50" style={{ left: `${target * 2}%` }} />
      </div>
      <span className={`num font-semibold ${foodCostTone(value, target)}`}>{pct(value)}</span>
    </div>
  );
}

const IncompleteBadge = () => (
  <span className="rounded-md bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-600 dark:text-amber-400" data-testid="recipe-incomplete-badge">{t("recipes.ingredients_a_ajouter")}</span>
);

export default function Recipes() {
  const { user } = useAuth();
  const target = user?.restaurant?.target_food_cost || 30;
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("margin");

  const [params, setParams] = useSearchParams();
  const [fillOpen, setFillOpen] = useState(false);
  const load = () => api.get("/recipes").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const incomplete = (items || []).filter((r) => r.incomplete).length;
  useEffect(() => {
    if (!items || params.get("fill") !== "1") return;
    if (incomplete > 0) setFillOpen(true);
    else setParams({}, { replace: true });
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps
  const closeFill = () => { setFillOpen(false); if (params.get("fill")) setParams({}, { replace: true }); };
  const rows = useMemo(() => (items || []).filter((r) => fold(r.name).includes(fold(q))).sort(SORTS[sort][1]), [items, q, sort]);

  return (
    <>
      <PageHeader eyebrow={t("recipes.fiches_techniques")} title={t("common.recettes")} description={t("recipes.cout_de_revient_cout_matiere_et_marge_de_c", { v0: target })}
        actions={<>
          <Button variant="outline" asChild data-testid="ai-recipe-btn"><Link to="/recipes/new?ai=1"><Sparkles className="mr-2 h-4 w-4 shrink-0 text-[hsl(var(--ai))]" /><span className="truncate">{t("common.assistant_ia")}</span></Link></Button>
          <Button asChild data-testid="add-recipe-btn"><Link to="/recipes/new"><Plus className="mr-2 h-4 w-4 shrink-0" /><span className="truncate"><span className="sm:hidden">{t("common.nouvelle")}</span><span className="hidden sm:inline">{t("recipes.new_recipe")}</span></span></Link></Button>
        </>} />
      {items && items.length === 0 ? (
        <EmptyState icon={ChefHat} title={t("common.aucune_recette")} description={t("recipes.creez_votre_premiere_fiche_technique_ou_de")} testId="recipes-empty">
          <Button asChild><Link to="/recipes/new?ai=1">{t("recipes.essayer_l_assistant_ia")}</Link></Button>
        </EmptyState>
      ) : (
        <>
          {incomplete > 0 && (
            <div className="mb-4 flex flex-col gap-3 rounded-xl border border-[hsl(var(--ai)/0.35)] bg-[hsl(var(--ai)/0.06)] p-4 sm:flex-row sm:items-center" data-testid="recipes-ai-fill-banner">
              <Sparkles className="hidden h-5 w-5 shrink-0 text-[hsl(var(--ai))] sm:block" />
              <p className="min-w-0 flex-1 text-sm"><span className="font-semibold">{incomplete} {t("recipes.plat_s_sans_ingredients")}</span> <span className="text-muted-foreground">{t("recipes.l_ia_peut_tous_les_completer_d_un_coup_pou")}</span></p>
              <Button onClick={() => setFillOpen(true)} className="shrink-0" data-testid="recipes-ai-fill-btn"><Sparkles className="mr-2 h-4 w-4" />{t("recipes.completer_avec_l_ia")}</Button>
            </div>
          )}
          <div className="mb-4 grid grid-cols-[1fr_auto] gap-2 sm:flex sm:gap-3">
            <div className="relative min-w-0 flex-1 sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder={t("recipes.rechercher")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="recipes-search-input" />
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-36 sm:w-64" data-testid="recipes-sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(SORTS).map(([k, [l]]) => <SelectItem key={k} value={k}>{typeof l === "function" ? l() : l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2 md:hidden" data-testid="recipe-mobile-list">
            {rows.map((r) => (
              <Link key={r.id} to={`/recipes/${r.id}`} className="block rounded-xl border bg-card p-3.5 active:bg-muted/50" data-testid={`recipe-card-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{r.name}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{catLabel(r.category)} · {eur(r.selling_price)} {t("common.ht_short")}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="num text-sm font-semibold">{r.incomplete ? "—" : eur(r.gross_margin)}</p>
                    <p className="text-[11px] text-muted-foreground">{t("common.margin_lower")}</p>
                  </div>
                </div>
                {r.incomplete ? <p className="mt-3"><IncompleteBadge /></p> : <div className="mt-3 flex items-center gap-3">
                  <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                    <div className={`h-full ${r.food_cost_pct <= target ? "bg-emerald-500" : r.food_cost_pct <= target + 5 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${Math.min(100, r.food_cost_pct * 2)}%` }} />
                    <div className="absolute top-0 h-full w-px bg-foreground/50" style={{ left: `${target * 2}%` }} />
                  </div>
                  <span className={`num text-xs font-semibold ${foodCostTone(r.food_cost_pct, target)}`}>{t("common.fc_short")} {pct(r.food_cost_pct)}</span>
                </div>}
              </Link>
            ))}
          </div>
          <div className="hidden overflow-x-auto rounded-xl border bg-card md:block">
            <Table data-testid="recipe-list">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("recipes.plat")}</TableHead>
                  <TableHead className="text-right">{t("recipes.prix_ht")}</TableHead>
                  <TableHead className="hidden text-right sm:table-cell">{t("recipes.cout")}</TableHead>
                  <TableHead className="text-right">{t("common.cout_matiere")}</TableHead>
                  <TableHead className="text-right">{t("recipes.marge")}</TableHead>
                  <TableHead className="hidden text-right md:table-cell">{t("recipes.marge_2")}</TableHead>
                  <TableHead className="hidden text-right lg:table-cell">{t("recipes.cout_30_j")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => nav(`/recipes/${r.id}`)} data-testid={`recipe-row-${r.id}`}>
                    <TableCell>
                      <p className="font-medium">{r.name}</p>
                      <p className="text-xs text-muted-foreground">{catLabel(r.category)} · {r.ingredients.length} {t("recipes.ingredient_s")}</p>
                    </TableCell>
                    <TableCell className="num text-right">{eur(r.selling_price)}</TableCell>
                    <TableCell className="num hidden text-right text-muted-foreground sm:table-cell">{eur(r.cost)}</TableCell>
                    <TableCell className="text-right">{r.incomplete ? <IncompleteBadge /> : <FoodCostBar value={r.food_cost_pct} target={target} />}</TableCell>
                    <TableCell className="num text-right font-semibold">{r.incomplete ? "—" : eur(r.gross_margin)}</TableCell>
                    <TableCell className="num hidden text-right md:table-cell">{r.incomplete ? "—" : pct(r.gross_margin_pct)}</TableCell>
                    <TableCell className="hidden text-right lg:table-cell"><ChangeBadge value={r.cost_change_pct} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
      {fillOpen && <AiFillDialog open={fillOpen} onOpenChange={(o) => !o && closeFill()} target={target} onDone={() => { closeFill(); load(); }} />}
    </>
  );
}
